<template>
  <v-container fluid class="pa-4">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-factory</v-icon>
            Modul proizvodnje
          </v-card-title>

          <v-stepper v-model="step" vertical>
            <!-- Step 1: Odabir polaznih artikala -->
            <v-stepper-step :complete="step > 1" step="1" editable>
              Odabir polaznih artikala
              <small>Odaberite skladište i artikle koje koristite</small>
            </v-stepper-step>
            <v-stepper-content step="1">
              <source-items-selector
                v-model="sourceItems"
                @warehouse-selected="sourceWarehouse = $event"
              />
              <v-btn
                color="primary"
                @click="validateStep1"
                :disabled="!canProceedStep1"
              >
                Nastavi
              </v-btn>
            </v-stepper-content>

            <!-- Step 2: Definicija proizvoda -->
            <v-stepper-step :complete="step > 2" step="2" editable>
              Definicija proizvoda
              <small>Odredite proizvode i njihove recepture</small>
            </v-stepper-step>
            <v-stepper-content step="2">
              <multiple-products-definition
                v-model="outputProducts"
                :source-items="sourceItems"
                @balance-status="balanceStatus = $event"
              />

              <div class="d-flex align-center mt-3">
                <v-btn text @click="step = 1" class="mr-2">Nazad</v-btn>
                <v-btn
                  color="primary"
                  @click="step = 3"
                  :disabled="!canProceedStep2"
                >
                  Nastavi
                </v-btn>
                <v-tooltip v-if="!canProceedStep2 && outputProducts.length > 0" right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="warning" class="ml-2" v-bind="attrs" v-on="on">
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>Materijali moraju biti potpuno balansirani prije nastavka</span>
                </v-tooltip>
              </div>
            </v-stepper-content>

            <!-- Step 3: Pregled i potvrda -->
            <v-stepper-step step="3">
              Pregled i potvrda
              <small>Pregledajte i potvrdite proizvodnju</small>
            </v-stepper-step>
            <v-stepper-content step="3">
              <production-summary
                :source-items="sourceItems"
                :source-warehouse="sourceWarehouse"
                :output-products="outputProducts"
                :balance-status="balanceStatus"
              />
              <v-btn text @click="step = 2" class="mr-2">Nazad</v-btn>
              <v-btn
                color="success"
                @click="executeProduction"
                :loading="loading"
                :disabled="!canExecuteProduction"
              >
                <v-icon left>mdi-check</v-icon>
                Izvrši proizvodnju
              </v-btn>
            </v-stepper-content>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success dialog -->
    <v-dialog v-model="successDialog" max-width="500">
      <v-card>
        <v-card-title class="success white--text">
          <v-icon left color="white">mdi-check-circle</v-icon>
          Proizvodnja uspješna
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Proizvodnja je uspješno izvršena!</p>
          <div v-for="(product, index) in outputProducts" :key="index" class="mb-1">
            <p v-if="product.item" class="mb-0">Proizvedeno: <strong>{{ Number(product.quantity).toFixed(2) }} {{ product.item.unit }}</strong> od <strong>{{ product.item.name }}</strong></p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" text @click="resetProduction">
            Nova proizvodnja
          </v-btn>
          <v-btn color="primary" text @click="successDialog = false">
            Zatvori
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import SourceItemsSelector from './production/SourceItemsSelector.vue'
import MultipleProductsDefinition from './production/MultipleProductsDefinition.vue'
import ProductionSummary from './production/ProductionSummary.vue'

export default {
  name: 'Production',
  components: {
    SourceItemsSelector,
    MultipleProductsDefinition,
    ProductionSummary
  },
  data () {
    return {
      step: 1,
      loading: false,
      successDialog: false,

      // Step 1 data
      sourceWarehouse: null,
      sourceItems: [],

      // Step 2 data
      outputProducts: [],
      balanceStatus: {
        isBalanced: false,
        hasUnused: false,
        hasOverused: false,
        materialBalance: []
      },

      // Mock warehouses - u buducnosti ce doci iz API-ja
      warehouses: [
        { id: 1, name: 'Glavno skladište', location: 'Zagreb' },
        { id: 2, name: 'Skladište sirovine', location: 'Zagreb' },
        { id: 3, name: 'Skladište gotovih proizvoda', location: 'Split' },
        { id: 4, name: 'Hladnjača', location: 'Rijeka' }
      ]
    }
  },
  computed: {
    canProceedStep1 () {
      return this.sourceWarehouse && this.sourceItems.length > 0 &&
             this.sourceItems.every(item => item.item && item.quantity > 0)
    },
    canProceedStep2 () {
      // Check that all products are properly defined
      const hasValidProducts = this.outputProducts.length > 0 &&
             this.outputProducts.every(p =>
               p.item &&
               p.quantity > 0 &&
               p.warehouse &&
               p.recipe.length > 0 &&
               p.recipe.every(r => r.sourceItem && r.quantityPerUnit > 0)
             )

      // Check that materials are balanced
      const isBalanced = this.balanceStatus.isBalanced

      return hasValidProducts && isBalanced
    },
    canExecuteProduction () {
      return this.canProceedStep2
    }
  },
  methods: {
    validateStep1 () {
      if (this.canProceedStep1) {
        this.step = 2
      }
    },
    executeProduction () {
      this.loading = true

      // Simulate API call
      setTimeout(() => {
        console.log('Executing production:', {
          sourceWarehouse: this.sourceWarehouse,
          sourceItems: this.sourceItems,
          outputProducts: this.outputProducts,
          balanceStatus: this.balanceStatus,
          materialWastage: this.balanceStatus.materialWastage
        })

        this.loading = false
        this.successDialog = true
      }, 1500)
    },
    resetProduction () {
      this.step = 1
      this.sourceWarehouse = null
      this.sourceItems = []
      this.outputProducts = []
      this.balanceStatus = {
        isBalanced: false,
        hasUnused: false,
        hasOverused: false,
        materialBalance: []
      }
      this.successDialog = false
    }
  }
}
</script>

<style scoped>
.v-stepper {
  box-shadow: none;
}
</style>
